import {compose} from 'redux';
import React from 'react';
import {createStructuredSelector} from 'reselect';
import {connect} from 'react-redux';
import {
    createV4AssistanceRequest,
    showAssistanceRequestDialog,
    startAssistanceRequest,
} from '../../../redux/actions/assistanceRequest.actions';
import {
    selectAssistanceRequestTargetAlarm,
    selectAssistanceRequestTileConfig,
    selectShowAssistantRequestDialog,
} from '../../../redux/selectors/assistanceRequest.selector';
import {Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import {useTranslation} from 'react-i18next';
import SlideUpTransition from '../../../components/SlideUpTransition';
import AssistanceRequestAction from '../../ScenariosPage/ActionItem/AssistanceRequestAction';
import cloneDeep from 'lodash/cloneDeep';
import {useSnackbar} from 'notistack';
import {selectCurrentUser} from '../../../redux/selectors/app.selectors';
import TextButton from '../../../components/Controls/TextButton';

// V4 rejects a blank title or message; the V3 path keeps its own check.
const validateConfig = ({name, text, recipients}, {v4} = {}) => {
    if (!(v4 ? name?.trim() : name)) return false;
    if (!(v4 ? text?.trim() : text)) return false;
    const {geofences, teams, devices} = recipients;
    return !!geofences.length || !!teams.length || !!devices.length;
};

// A copy: the form keeps editing the same object while the V4 send awaits its lookups.
export const sendRequestAction = (config, alarm) =>
    alarm ? createV4AssistanceRequest(alarm, cloneDeep(config)) : startAssistanceRequest(config);

export function mapDispatchToProps(dispatch) {
    return {
        close: () => dispatch(showAssistanceRequestDialog(false)),
        start: (c, alarm) => dispatch(sendRequestAction(c, alarm))
    };
}

const mapStateToProps = createStructuredSelector({
    show: selectShowAssistantRequestDialog(),
    tileConfig: selectAssistanceRequestTileConfig(),
    alarm: selectAssistanceRequestTargetAlarm(),
    user: selectCurrentUser()
});

const AssistanceRequestDialog = compose(
    connect(mapStateToProps, mapDispatchToProps)
)(({user, show, close, start, tileConfig, alarm}) => {
    const [t] = useTranslation();
    const {enqueueSnackbar} = useSnackbar()
    const [config, setConfig] = React.useState({});
    const [, setUpdate] = React.useState(0);
    // From an alarm the form starts empty every time; elsewhere it keeps its values, as before.
    // Flagged here because the target alarm is gone from the store once the dialog closes.
    const clearOnExit = React.useRef(false);
    const finish = () => {
        clearOnExit.current = !!alarm;
        close();
    };
    React.useEffect(() => {
        if (show) setTimeout(() => setConfig({
            ...config,
            name: config?.name || t('newAssistanceRequest')
        }));
    }, [show, t, setConfig]); // eslint-disable-line

    const configValid = validateConfig(config, {v4: !!alarm});

    return (
        <Dialog
            open={!!show}
            onClose={(e, reason) => {
                if (reason === 'backdropClick') return false;
                finish();
            }}
            TransitionComponent={SlideUpTransition}
            TransitionProps={{
                onExited: () => {
                    if (!clearOnExit.current) return;
                    clearOnExit.current = false;
                    setConfig({});
                },
            }}
            fullWidth
            maxWidth="sm">
            <DialogTitle>{t('newAssistanceRequest')}</DialogTitle>
            <DialogContent>
                <AssistanceRequestAction
                    isDeviceTrigger={false}
                    v4Target={!!alarm}
                    tileConfig={tileConfig}
                    hideOffline
                    showStatus
                    user={user}
                    showQuickTexts
                    placeholders={{
                        date: 'dateOfEvent',
                        time: 'timeOfEvent'
                    }}
                    onChange={c => {
                        setConfig(c);
                        setUpdate(Date.now()); //Force rerender
                    }}
                    config={config}
                />
            </DialogContent>
            <DialogActions>
                <TextButton onClick={finish}>{t('cancel')}</TextButton>
                <TextButton
                    onClick={() => {
                        start(config, alarm);
                        enqueueSnackbar(t('startingAssistanceRequest'));
                        finish();
                    }}
                    color="primary"
                    disabled={!configValid}>{t('sendRequest')}</TextButton>
            </DialogActions>
        </Dialog>
    );

});

export default AssistanceRequestDialog;